import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon } from '@heroicons/react/24/outline';

interface Props {
  id: string;
  title?: string;
  fallback: string; // 无标题时显示的内容（如 id 前8位）
  onSave: (id: string, title: string) => Promise<void>;
}

/**
 * 内联标题编辑器 - 点击后变为 input，失焦或回车保存
 */
export default function MusicTitleEditor({ id, title, fallback, onSave }: Props) {
  const { t } = useTranslation('generation');
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 外部 title 变化时同步
  useEffect(() => {
    if (!editing) setValue(title || '');
  }, [title, editing]);

  const startEdit = () => {
    setValue(title || '');
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commit = async () => {
    const trimmed = value.trim();
    setEditing(false);
    if (trimmed === (title || '')) return; // 无变化
    setSaving(true);
    try {
      await onSave(id, trimmed);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') { setValue(title || ''); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        maxLength={100}
        autoFocus
        className="text-sm font-medium text-gray-50 bg-bg-form border border-primary/50 rounded px-1.5 py-0.5 outline-none w-40 min-w-0"
        placeholder={t('generation:music.titleEditorPlaceholder')}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      title={t('generation:music.titleEditorClickEdit')}
      className="group flex items-center gap-1 min-w-0 cursor-pointer"
    >
      <span className={`text-sm font-medium truncate ${title ? 'text-gray-200' : 'text-gray-500'}`}>
        {saving ? t('generation:music.titleEditorSaving') : (title || fallback)}
      </span>
      <PencilIcon className="w-3 h-3 text-gray-600 group-hover:text-primary flex-shrink-0 transition-colors" />
    </button>
  );
}
